import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { getOrgConfig } from '../../utils/orgResolution';
import useSencoPupils from '../../hooks/useSencoPupils';
import { useRmsState } from '../../context/RmsStateContext';
import { STATUS, TRIAGE_FORM_KEYS } from '../../utils/referralStatus';
import {
  harbourFormKeysFor,
  harbourFormRoute,
  harbourFormTitle,
  countSchemaQuestions,
  estimatedMinutes,
  buildHomeRows,
  TRIAGE_FORM_TITLES,
} from '../../utils/harbourForms';
import { buildFamilyChild, familyUserName, guardianConsentState, childFirstName, referralForLine, schoolStillToFinishLine } from '../../utils/familyPortal';
import { recordFormContext } from '../../utils/serverFormContext';
import {
  recordRequiresSubmissionConfirmation,
  isRoleSubmissionConfirmed,
  allRequiredRespondentsConfirmed,
  canConfirmSubmission,
} from '../../utils/submissionConfirmation';
import { apiConfirmSubmission } from '../../utils/rmsApi';
import SubmitFormsModal from '../../components/SubmitFormsModal';
import FamilyPortalShell from './FamilyPortalShell';
import FamilyLoadError from './FamilyLoadError';
import FamilyChildCard from './FamilyChildCard';
import { AnswersPrintView } from '../harbour/SchoolAnswersPrintPage';
import useIsDesktop from './useIsDesktop';
import HarbourButton from '../../components/harbour/HarbourButton';
import HarbourFlyout from '../../components/harbour/HarbourFlyout';
import HarbourIcon from '../../components/harbour/HarbourIcon';
import HarbourNotice from '../../components/harbour/HarbourNotice';
import HarbourPathwayMark from '../../components/harbour/HarbourPathwayMark';
import HarbourProgressSegments from '../../components/harbour/HarbourProgressSegments';
import HarbourStatusChip from '../../components/harbour/HarbourStatusChip';
import HarbourTextLink from '../../components/harbour/HarbourTextLink';
import '../../components/harbour/harbour.css';
import './familyFormsHome.css';

/*
 * Parent or carer forms homepage (SEND & ND Access Portal, TDI-53).
 * Route: /rms/family/child/:patientRecordId/forms/:pathway ('sen' | 'referral').
 *
 * Design: Figma UAXOnhR1ZvlsNl4diy00xX — mobile frames 3311:2395 / 3339:5533 /
 * 3311:2544 / 3311:2686 (SEN not started → all done), 3311:2843 / 3339:5831
 * (referral); desktop 3427:7089, 3451:142250, 3452:143540, 3452:143768,
 * 3453:143362, 3453:144272. The school-side forms homepage machinery
 * (harbourForms.js, HarbourFormsHomeFlyout) is reused, parameterised by the
 * guardian role: counts and time estimates come from the LIVE schemas, the
 * sequential lock opens one form at a time, and finished forms stay open.
 *
 * Mobile renders as a plain shell page with the sticky Start/Continue bar.
 * Desktop (≥1100) renders the frames' composition: the Your children page
 * behind a scrim, with the homepage card in a wide (1688) task flyout —
 * pathway eyebrow, the child's name and the "For:" help line in the flyout
 * header, the all-done notice in the right rail, and the single gold action
 * on the next form's row.
 */

/** Slugs for the guardian-only triage forms (mirrors RmsStrengthsFormRoute). */
const TRIAGE_FORM_SLUGS = {
  theirEarlyLife: 'their-early-life',
  theirFamilyAndHomeLife: 'their-family-and-home-life',
  theirLifestyle: 'their-lifestyle',
  theirHistoryWithTheLaw: 'their-history-with-the-law',
  theirFamilyHistory: 'their-family-history',
  theirMedicalHistory: 'their-medical-history',
  theirMentalHealthHistory: 'their-mental-health-history',
};


/*
 * "Download your answers" (TDI-692) opens this same address with
 * ?print=answers in its own tab: the print view of the parent or carer's own
 * submitted answers to this page's forms, saved as a PDF from the browser.
 */
export default function FamilyFormsHomeRoute() {
  const [searchParams] = useSearchParams();
  const { pathway } = useParams();
  if (searchParams.get('print') === 'answers' && (pathway === 'sen' || pathway === 'referral')) {
    return <AnswersPrintView role="guardian" pathway={pathway} />;
  }
  return <FamilyFormsHomePage />;
}

function FamilyFormsHomePage() {
  const navigate = useNavigate();
  const { patientRecordId, pathway } = useParams();
  const { user, claims } = useAuth();
  const claimsOrgId = claims?.rmsOrgId;
  const orgId = claimsOrgId || getOrgConfig().orgId;
  const { ensurePreSchema } = useRmsState();
  const { pupils, loading, error, reload, orgHfpInstrument = null } = useSencoPupils({ orgId, claimsOrgId });
  const isDesktop = useIsDesktop();

  // School users (and anyone else) never belong here.
  useEffect(() => {
    if (claims && claims.userType && claims.userType !== 'RMS Legal Guardian') {
      navigate('/rms/dashboard', { replace: true });
    }
  }, [claims, navigate]);

  const pupil = useMemo(
    () => pupils.find((p) => p.id === patientRecordId) || null,
    [pupils, patientRecordId]
  );
  const patient = pupil?.patient || {};
  const senActive = Boolean(pupil?.sen && pupil.sen.stage !== 'stopped');
  // The family took their permission back for the SEND pathway (TDI-479):
  // their answers are kept and stay readable, but nothing can be added. Only
  // the SEND homepage reads as withdrawn; the referral's forms are its own.
  const senWithdrawn = pathway === 'sen'
    && Boolean(pupil?.sen && pupil.sen.stage === 'stopped' && pupil.sen.stopReason === 'consent_withdrawn');

  // The ordered form groups for this pathway. SEN is the seven HFP forms; the
  // referral homepage is the base referral forms ("Referral") plus everything
  // else the guardian answers ("Additional information": the HFP forms when
  // the SEND pathway is not already gathering them, then the seven
  // guardian-only history forms).
  const groups = useMemo(() => {
    if (!pupil) return [];
    // The record picks the HFP set: v3 for Harbour pupils, v2 otherwise.
    // (Read from `pupil`, already a dependency, so the memo stays stable.)
    const record = pupil.patient || null;
    if (pathway === 'sen') {
      // The record's instrument, or the organisation's setting while the
      // profile has not started (TDI-523), picks the strengths and needs set.
      return [{
        key: 'sen',
        title: null,
        formKeys: harbourFormKeysFor('sen', { patient: record, orgSetting: orgHfpInstrument }),
      }];
    }
    const opts = {
      patient: record,
      intakeConcern: patient.intakeConcern || null,
      senActive,
      schoolPhase: patient.schoolPhase || null,
    };
    // Diana's "Referral forms / Homepage": four forms under Referral, the
    // seven history forms under Additional information, eleven in all. The
    // strengths and needs forms are on neither group for any child — they are
    // the SEND pathway's, and have their own homepage.
    return [
      { key: 'referral', title: 'Referral', formKeys: harbourFormKeysFor('referral', opts) },
      { key: 'additional', title: 'Additional information', formKeys: [...TRIAGE_FORM_KEYS] },
    ];
  }, [pupil, pathway, patient.intakeConcern, patient.schoolPhase, senActive, orgHfpInstrument]);

  const formKeys = useMemo(() => groups.flatMap((g) => g.formKeys), [groups]);

  // Live schemas drive the honest counts and time estimates — never hardcoded.
  const [schemas, setSchemas] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(formKeys.map(async (formKey) => {
        try {
          const schema = await ensurePreSchema({ orgId, formKey });
          return [formKey, schema];
        } catch (_) {
          return [formKey, null];
        }
      }));
      if (!cancelled) setSchemas(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [formKeys, ensurePreSchema, orgId]);

  const formProgress = pupil?.formProgress || {};
  const rows = useMemo(
    () => buildHomeRows(formKeys, formProgress, 'guardian'),
    [formKeys, formProgress]
  );
  const rowByKey = useMemo(
    () => Object.fromEntries(rows.map((row) => [row.formKey, row])),
    [rows]
  );
  const doneCount = rows.filter((r) => r.status === 'done').length;
  const allDone = rows.length > 0 && doneCount === rows.length;
  const nextRow = rows.find((r) => r.status !== 'done') || null;

  const titleFor = (formKey) => (
    TRIAGE_FORM_TITLES[formKey]
      ? (schemas[formKey] && schemas[formKey].title) || TRIAGE_FORM_TITLES[formKey]
      : harbourFormTitle(formKey, schemas[formKey])
  );
  const routeFor = (formKey) =>
    harbourFormRoute(patientRecordId, formKey) ||
    `/rms/patient/${encodeURIComponent(patientRecordId)}/forms/${TRIAGE_FORM_SLUGS[formKey]}`;
  // The child's sex and school phase leave parts out of some forms (TDI-709,
  // TDI-708), so the counts follow them as the forms do.
  const childContext = recordFormContext(patient);
  const metaFor = (formKey) => {
    const schema = schemas[formKey];
    const count = countSchemaQuestions(schema, 'guardian', 'school', childContext);
    const minutes = estimatedMinutes(schema, count);
    return [
      count ? `${count} question${count === 1 ? '' : 's'}` : null,
      minutes ? `about ${minutes} minute${minutes === 1 ? '' : 's'}` : null,
    ]
      .filter(Boolean)
      .join(' · ');
  };
  const totalMinutes = formKeys.reduce((sum, formKey) => {
    const schema = schemas[formKey];
    const count = countSchemaQuestions(schema, 'guardian', 'school', childContext);
    return sum + (estimatedMinutes(schema, count) || 0);
  }, 0);

  // Confirm and submit, per pathway (TDI-415; TDI-651): this homepage's own
  // pathway is the one the parent or carer submits here, and its
  // confirmation locks only these forms. Hooks sit above the early returns.
  const confirmPathway = pathway === 'sen' ? 'sen' : 'referral';
  const needsConfirmStep = recordRequiresSubmissionConfirmation(patient);
  const guardianConfirmed = isRoleSubmissionConfirmed(patient, 'guardian', confirmPathway);
  const everyoneConfirmed = allRequiredRespondentsConfirmed(patient, confirmPathway);
  const canConfirm = Boolean(pupil) && allDone && needsConfirmStep && !guardianConfirmed
    && canConfirmSubmission({ patient, formProgress, role: 'guardian', pathway: confirmPathway });
  // "All forms answered or coming back" (5226:392640 / 5226:392831): the
  // submit pop-up opens by itself when the parent arrives with every form
  // answered; "Check my answers first" closes it until they ask again.
  const [submitPopupOpen, setSubmitPopupOpen] = useState(false);
  const [submitPopupShown, setSubmitPopupShown] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  useEffect(() => {
    if (canConfirm && !loading && !submitPopupShown) {
      setSubmitPopupOpen(true);
      setSubmitPopupShown(true);
    }
  }, [canConfirm, loading, submitPopupShown]);
  const onSubmitForms = async () => {
    setSubmitBusy(true);
    setSubmitFailed(false);
    try {
      await apiConfirmSubmission({ orgId, patientRecordId, pathway: confirmPathway });
      setSubmitPopupOpen(false);
      reload({ force: true });
    } catch (_e) {
      setSubmitFailed(true);
    } finally {
      setSubmitBusy(false);
    }
  };

  // How far through the current form the guardian is (drafts cap at 99).
  const nextPercentRaw = nextRow
    ? Number(formProgress[`${nextRow.formKey}_guardian`] ?? formProgress[nextRow.formKey] ?? 0)
    : 0;
  const currentFraction = Number.isFinite(nextPercentRaw)
    ? Math.max(0, Math.min(nextPercentRaw, 99)) / 100
    : 0;

  const userName = familyUserName(user, pupils);
  const back = { to: `/rms/family/child/${patientRecordId}`, label: 'Back' };
  const firstName = childFirstName(pupil);
  const tone = pathway === 'sen' ? 'sen' : 'referral';
  const forLine = pathway === 'referral' ? referralForLine(patient.intakeConcern) : null;

  if (pathway !== 'sen' && pathway !== 'referral') {
    return <Navigate to={`/rms/family/child/${patientRecordId}`} replace />;
  }

  if (loading) {
    return (
      <FamilyPortalShell userName={userName} back={back}>
        <p className="family-loading" role="status">Loading…</p>
      </FamilyPortalShell>
    );
  }
  if (error || !pupil) {
    return (
      <FamilyPortalShell userName={userName} back={back}>
        <FamilyLoadError onRetry={() => reload()} />
      </FamilyPortalShell>
    );
  }

  // The forms open only after the guardian has confirmed consent for this
  // pathway — anyone arriving earlier is taken to the consent page.
  const pathwayData = pathway === 'sen' ? patient.senPathway : patient.referralPathway;
  if (guardianConsentState(pathwayData) !== 'confirmed' && !senWithdrawn) {
    return <Navigate to={`/rms/family/child/${patientRecordId}/consent/${pathway}`} replace />;
  }

  // "Submit your forms · Parent or carer" (READY FOR DEV): S-1 before the
  // parent submits, S-3 once they have (5226:392674 / 5226:392863). Records
  // that predate the confirm step keep their wording: nothing locks there.
  const submittedLocked = allDone && needsConfirmStep && guardianConfirmed;
  const heading = !allDone
    ? `${doneCount} of ${rows.length} forms done`
    : submittedLocked
      ? `All ${rows.length} forms submitted`
      : needsConfirmStep && !senWithdrawn
        ? `All ${rows.length} forms are answered. Check and submit them.`
        : `All ${rows.length} forms are complete`;
  const subline = senWithdrawn
    ? 'Your answers are kept, and you can still read them.'
    : submittedLocked
      // S-3 adds "and download a copy"; the parent portal has no download of
      // the answers yet, so the sentence stops where the page does.
      ? 'You’ve submitted your answers, so they’re now locked and can’t be changed. You can still read them.'
      : allDone
        ? 'Thank you. You can open any form to review your answers.'
        : totalMinutes > 0
          ? `About ${totalMinutes} minutes in total. Your answers save as you go.`
          : 'Your answers save as you go.';
  // When the parent is done but the school is not, say who still has forms
  // to finish (Jasmine, 6 Oct 2026) and what happens once they do.
  const schoolDone = pathway === 'sen'
    ? pupil.senSchoolStatus === STATUS.COMPLETED
    : pupil.schoolStatus === STATUS.COMPLETED;
  // Records gated on the confirm-and-submit step (TDI-415/TDI-418) describe
  // that step instead of the legacy "sent automatically" promise; legacy
  // records keep today's copy, because for them it stays true.
  const showConfirmCta = canConfirm && !senWithdrawn;
  let noticeTitle;
  let noticeBody;
  if (needsConfirmStep) {
    if (!everyoneConfirmed) {
      // The right-hand notice on S-1 and S-3 (5226:392831, 5226:392863,
      // 5226:392929, 5226:393001).
      noticeTitle = pathway === 'sen'
        ? `When you have both submitted, you can both see ${firstName}’s profile, with home and school side by side.`
        : 'The referral is sent automatically once you and the school have both submitted your forms.';
      noticeBody = null;
    } else {
      noticeTitle = pathway === 'sen'
        ? `You can both see ${firstName}’s profile, with home and school side by side.`
        : 'The referral has been sent.';
      noticeBody = null;
    }
  } else {
    noticeTitle = !schoolDone
      ? schoolStillToFinishLine(pupil)
      : pathway === 'sen'
        ? `When you have both finished, you can both see ${firstName}’s profile, with home and school side by side.`
        : 'The referral is sent automatically once you and the school have both finished your forms.';
    noticeBody = !schoolDone
      ? (pathway === 'sen'
        ? `Once they have, you can both see ${firstName}’s profile, with home and school side by side.`
        : 'Once they have, the referral is sent automatically.')
      : null;
  }
  const nextLabel = nextRow && nextRow.status === 'inProgress' ? 'Continue' : 'Start';
  const nextRoute = nextRow && !senWithdrawn ? routeFor(nextRow.formKey) : null;

  const eyebrowLabel = pathway === 'sen' ? 'SEND pathway' : 'Referral pathway';
  const noticeEl = senWithdrawn ? (
    <HarbourNotice kind="info" className="ffh-notice" title="You took back your permission for the SEND pathway.">
      Your answers are kept, and you can still read them.
    </HarbourNotice>
  ) : allDone && (
    <HarbourNotice kind="info" className="ffh-notice" title={noticeTitle}>
      {noticeBody}
    </HarbourNotice>
  );
  const submitPopup = submitPopupOpen && showConfirmCta && (
    <SubmitFormsModal
      count={rows.length}
      actionKind="family"
      busy={submitBusy}
      error={submitFailed}
      onConfirm={onSubmitForms}
      onClose={() => { setSubmitPopupOpen(false); setSubmitFailed(false); }}
    />
  );

  // The homepage card is identical on both widths; only the chrome around it
  // changes (shell page on mobile, task flyout on desktop).
  let number = 0;
  const formsCard = (
        <section className="ffh-card" aria-label={pathway === 'sen' ? 'SEND forms' : 'Referral forms'}>
          <div className="ffh-summary">
            <div className="ffh-summary__head">
              <h1 className="ffh-summary__title">{heading}</h1>
              {showConfirmCta && (
                // Desktop only (5226:392831): the phone carries this action
                // in its sticky bar, so one gold action stays the rule.
                <span className="ffh-only-desktop ffh-summary__action">
                  <HarbourButton kind="family" onClick={() => setSubmitPopupOpen(true)}>
                    Confirm and submit all responses
                  </HarbourButton>
                </span>
              )}
            </div>
            <p className="ffh-summary__sub">{subline}</p>
            {/* All done: a readable copy of just their own answers (TDI-692,
                3453:144272 laptop, 3339:5831 phone). Not while the confirm
                step is still to do, as on the school side; taking permission
                back only stops changes, so the answers stay downloadable. */}
            {allDone && !showConfirmCta && (
              <HarbourButton
                kind="secondary"
                leadingIcon="download"
                className="ffh-summary__download"
                onClick={() => window.open(
                  `/rms/family/child/${encodeURIComponent(patientRecordId)}/forms/${pathway}?print=answers`,
                  '_blank',
                  'noopener'
                )}
              >
                Download your answers
              </HarbourButton>
            )}
            <HarbourProgressSegments
              tone={tone}
              total={rows.length}
              doneCount={doneCount}
              currentFraction={currentFraction}
            />
          </div>

          {groups.map((group) => (
            <div key={group.key} className="ffh-group">
              {group.title && <h2 className="ffh-group__title">{group.title}</h2>}
              <ol className="ffh-rows">
                {group.formKeys.map((formKey) => {
                  number += 1;
                  const row = rowByKey[formKey];
                  if (!row) return null;
                  const done = row.status === 'done';
                  // Withdrawn: nothing is locked away, but nothing can be
                  // added either, so every row is read-only and the next
                  // form is no longer offered.
                  const locked = !row.open && !senWithdrawn;
                  // Submitted (S-3): the answers are locked, so each row reads
                  // "Submitted" with a View link, like a withdrawn pathway.
                  const readOnly = senWithdrawn || submittedLocked;
                  const isNext = !readOnly && Boolean(nextRow && nextRow.formKey === formKey);
                  const title = titleFor(formKey);
                  const meta = metaFor(formKey);
                  const route = routeFor(formKey);
                  const rowClasses = [
                    'ffh-row',
                    done ? 'ffh-row--done' : '',
                    locked ? 'ffh-row--locked' : '',
                    isNext ? 'ffh-row--next' : '',
                  ].filter(Boolean).join(' ');
                  return (
                    <li key={formKey} className={rowClasses}>
                      <span className="ffh-row__disc" aria-hidden="true">
                        <span className="ffh-row__disc-number">{number}</span>
                        {done && (
                          <span className="ffh-row__disc-check">
                            <HarbourIcon name="check" size={16} />
                          </span>
                        )}
                      </span>
                      <span className="ffh-row__text">
                        {locked || readOnly ? (
                          <span className="ffh-row__title">{title}</span>
                        ) : (
                          <Link className="ffh-row__title" to={done ? `${route}?mode=review` : route}>
                            {title}
                          </Link>
                        )}
                        {meta && <span className="ffh-row__meta">{meta}</span>}
                      </span>
                      <span className="ffh-row__chip">
                        {done && (
                          <HarbourStatusChip
                            status="done"
                            tone={tone}
                            label={submittedLocked ? 'Submitted' : undefined}
                          />
                        )}
                        {!done && row.status === 'inProgress' && (
                          <HarbourStatusChip status="inProgress" tone="neutral" />
                        )}
                        {isNext && row.status === 'notStarted' && (
                          <span className="ffh-only-desktop">
                            <HarbourStatusChip status="notStarted" tone="neutral" />
                          </span>
                        )}
                        {locked && (
                          <span className="ffh-only-desktop">
                            <HarbourStatusChip status="locked" tone="neutral" />
                          </span>
                        )}
                      </span>
                      <span className="ffh-row__actions">
                        {done && (
                          <HarbourTextLink to={`${route}?mode=review`}>{readOnly ? 'View' : 'Review your answers'}</HarbourTextLink>
                        )}
                        {readOnly && !done && row.status === 'inProgress' && (
                          <HarbourTextLink to={`${route}?mode=review`}>View</HarbourTextLink>
                        )}
                        {isNext && (
                          <HarbourButton
                            kind="family"
                            trailingIcon="arrow-right"
                            onClick={() => navigate(route)}
                          >
                            {row.status === 'inProgress' ? 'Continue' : 'Start'}
                          </HarbourButton>
                        )}
                      </span>
                      <span className="ffh-row__trailing" aria-hidden={!locked}>
                        {locked ? (
                          <HarbourIcon name="lock" size={24} title="Locked" />
                        ) : (
                          <HarbourIcon name="chevron-right" size={24} />
                        )}
                      </span>
                    </li>
                  );
                })}
              </ol>
            </div>
          ))}
        </section>
  );

  // Desktop: the task flyout over Your children (3427:7089 and siblings).
  if (isDesktop) {
    const allChildren = (pupils || []).map(buildFamilyChild).filter(Boolean);
    return (
      <FamilyPortalShell userName={userName} maxWidth="full">
        <div className="family-page-head">
          <h1 className="family-page-head__title family-page-head__title--home">Your children</h1>
          <p className="family-page-head__intro">Choose your child’s name to see their details and questions.</p>
        </div>
        <ul className="family-child-cards">
          {allChildren.map((c) => (
            <li key={c.id}><FamilyChildCard child={c} linkState={{ stay: true }} /></li>
          ))}
        </ul>

        <HarbourFlyout
          open
          width="wide"
          title={pupil.name}
          year={pupil.yearGroup}
          eyebrow={{ tone, label: eyebrowLabel }}
          help={forLine || undefined}
          onClose={() => navigate(`/rms/family/child/${patientRecordId}`, { state: { stay: true } })}
          closeLabel="Close"
        >
          <div className={`ffh ffh--${tone} ${allDone ? 'ffh--done' : ''}`.trim()}>
            {noticeEl}
            {formsCard}
          </div>
          {/* Inside the flyout: it is a Radix dialog that hides its siblings
              from assistive tech, so a dialog beside it would be unreachable. */}
          {submitPopup}
        </HarbourFlyout>
      </FamilyPortalShell>
    );
  }

  // Mobile: the plain shell page with the sticky action bar.
  return (
    <FamilyPortalShell userName={userName} back={back}>
      <div className={`ffh ffh--${tone} ${allDone ? 'ffh--done' : ''}`.trim()}>
        <header className="ffh-head">
          <p className="ffh-eyebrow">
            <HarbourPathwayMark pathway={tone} />
            <span>
              {eyebrowLabel}
              <span className="ffh-eyebrow__name">{` · ${pupil.name}`}</span>
            </span>
          </p>
          {forLine && <p className="ffh-for">{forLine}</p>}
        </header>

        {noticeEl}
        {formsCard}

        {!allDone && nextRoute && (
          <div className="ffh-bar">
            <HarbourButton
              kind="family"
              trailingIcon="arrow-right"
              className="ffh-bar__cta"
              onClick={() => navigate(nextRoute)}
            >
              {nextLabel}
            </HarbourButton>
          </div>
        )}

        {showConfirmCta && (
          <div className="ffh-bar">
            <HarbourButton
              kind="family"
              className="ffh-bar__cta"
              onClick={() => setSubmitPopupOpen(true)}
            >
              Confirm and submit all responses
            </HarbourButton>
          </div>
        )}
        {submitPopup}
      </div>
    </FamilyPortalShell>
  );
}
